import { formatUseLanguageCode } from '../business/helpers';
import { type Locale } from '../store/i18nSlice';
import { addHoursTo, getCurrentDate, getDifferenceInMinutes, getEndOfDay, setHoursTo, setMinutesTo } from './date/date-utils';

export const letterRegex = /[a-zA-Z]/g;

export const minutesToMidnight = () => getDifferenceInMinutes({ firstDate: getEndOfDay(getCurrentDate()), secondDate: getCurrentDate() });

export const getFuturePledgeDate = (date: Date) => {
  if (date.getMinutes() > 30) {
    return addHoursTo({ date: setMinutesTo({ date, quantity: 0 }), quantity: 1 });
  }

  return setMinutesTo({ date, quantity: 30 });
};

export const getSameDateWithActualHour = (date: Date) =>
  setHoursTo({ date: setMinutesTo({ date, quantity: getCurrentDate().getMinutes() }), quantity: getCurrentDate().getHours() });

export const blockedExtensions = new Set([
  'dll',
  'exe',
  'app',
  'swf',
  'sys',
  'jar',
  'gzquar',
  'lnk',
  'zix',
  'js',
  'scr',
  'ws',
  'bat',
  'aru',
  'bin',
  'ozd',
  'wmf',
  'com',
  'ocx',
  'class',
  'vbs',
  'drv',
  'chm',
  'shs',
  'pgm',
  'dev',
  'xlm',
  'vba',
  'pif',
  'xnxx',
  'vbe',
  'vxd',
  'tsa',
  'boo',
  'tps',
  'sop',
  'hlp',
  '386',
  'vb',
  'pcx',
  'bkd',
  'rhk',
  'exe1',
  'cih',
  'lik',
  'osa',
  'dyz',
  'dyv',
  'kcd',
  'vbx',
  'wsc',
  '.9',
  's7p',
  'dxz',
  'dlb',
  'php3',
  'dom',
  'mfu',
  'hlw',
  'mjg',
  'rsc_tmp',
  'cla',
  'bup',
  'mjz',
  'bhx',
  'upa',
  'mcq',
  'dli',
  'xir',
  'fnr',
  'xlv',
  'txs',
  'bxz',
  'xdu',
  'cxq',
  'ska',
  'wlpginstall',
  'wsh',
  'cfxxe',
  'tti',
  'qrn',
  'vexe',
  'dllx',
  'fag',
  'xtbl',
  'smtmp',
  'tko',
  'ceo',
  'uzy',
  'oar',
  'scr',
  'bll',
  'plc',
  'ssy',
  'dbd',
  'smm',
  'spam',
  'ce0',
  'zvz',
  'cc',
  'blf',
  'ctbl',
  'iws',
  'vzr',
  'nls',
  'hsq',
  'lkh',
  'aepl',
  'rna',
  'let',
  'hts',
  'aut',
  'delf',
  'buk',
  'fuj',
  'atm',
  'ezt',
  'fjl',
  'bmw',
  'iva',
  'dx',
  'bps',
  'cyw',
  'pid',
  'bqf',
  'pr',
  'capxml',
  'qit',
  'xnt',
  'lok',
  'lpaq5',
]);

export const getHumanizeDurationLanguage = (appLanguage: Locale) => {
  if (appLanguage.language.toLowerCase() === 'zh') {
    return formatUseLanguageCode(appLanguage);
  }
  return appLanguage.language.toLowerCase();
};

export const printNewWindow = (newWindow: Window, file: Uint8Array, isMobile: boolean) => {
  if (!(file.buffer instanceof ArrayBuffer)) {
    throw new Error('Expected ArrayBuffer, got SharedArrayBuffer');
  }
  const blob = new Blob([file.buffer.slice(file.byteOffset, file.byteOffset + file.byteLength)], { type: 'application/pdf' });
  newWindow.location.replace(URL.createObjectURL(blob));
  !isMobile &&
    setTimeout(() => {
      newWindow.print();
    }, 200);
};

export const openDocumentUrl = (url: string, title: string, mimeType: string) =>
  fetch(url)
    .then(res => res.arrayBuffer())
    .then(result => {
      const newBlob = new Blob([result], { type: mimeType });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(newBlob);
      link.title = title;
      if (mimeType.toLowerCase().includes('pdf')) {
        link.target = '_blank';
      } else {
        link.download = title;
      }
      link.click();
    });

export const generateRandomDigits = (): string => {
  const characters = '1234567890';
  return Array.from(new Array(8)).reduce((acc: string) => acc + characters.charAt(Math.floor(Math.random() * 10)), '');
};

export const formatLocales = (availableLanguages: string[]): Locale[] =>
  availableLanguages
    .filter(availableLanguage => availableLanguage.split('-').length === 2)
    .map(availableLanguage => {
      const [language, country] = availableLanguage.split('-');
      return {
        language: language.toUpperCase(),
        country: country.toUpperCase(),
      };
    });

export const truncateId = (searchId: string) => searchId.substring(0, 16);

export const arrayUniqueByKey = (array: any[], key: string) => [...new Map(array.map(item => [item[key], item])).values()];

/**
 * Removes duplicate values from an array.
 *
 * @param array  The array to deduplicate.
 *
 * @returns The deduplicated array.
 *
 * @example
 *
 * const array = [1, 2, 2, 3, 4, 4, 5];
 * const deduplicatedArray = deduplicate(array);
 * // => [1, 2, 3, 4, 5]
 */
export function deduplicate<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

export function formatAPICLink(input: string) {
  const parsedInput = input.split(/https:\/\/p\.decathlon\.net\/apic\//i);
  return parsedInput[1] || input;
}

function wait(duration: number) {
  return new Promise(resolve => {
    setTimeout(resolve, duration);
  });
}

export function isLast(index: number, array: any[]) {
  return index === array.length - 1;
}

export function removeSpacesBetweenHyphen(text: string): string {
  return text.split(' - ').join('-');
}

/**
 * Throttles the execution to show a loading state for a minimum duration.
 * This is useful to avoid flickering when the data is loaded too quickly.
 */
export async function throttleToShowLoadingState() {
  const DELAY_IN_MILLISECONDS = 200;

  await wait(DELAY_IN_MILLISECONDS);
}

/**
 * Throttles the execution to wait for intervention operations.
 * This is useful to ensure that subsequent operations dependent on the intervention updates
 * have sufficient time to complete.
 */
export async function throtthleToWaitIntervention(delayInMs: number) {
  await wait(delayInMs);
}
